import Head from 'next/head';
import Link from 'next/link';
import { useRef, useState } from 'react';
import { useInViewport } from 'react-in-viewport';
import { useMediaQuery } from 'react-responsive';
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader
import Fade from 'react-reveal/Fade';
import validator from "validator";
import BackgroundScene from '../components/3d/MidiKeyboard';
import Button from "../components/inputs/Button";
import Input from "../components/inputs/Input";
import DemoPack from '../components/landing/DemoPack.tsx';
import Section from '../components/landing/Section';
import { Logo } from '../components/misc/Logo';
import { Paragraph, SectionTitle, Subtitle, Title } from '../components/misc/Typography';
import { writeEmail } from '../firebase/firestore';
import styles from '../styles/Home.module.css';
import { useScreenSize } from '../styles/media-queries';
import { removeLayout } from '../utils/pageHelpers';
import { useScrollPosition } from '@n8tb1t/use-scroll-position'
import SwerveLine, { VerticalSwerveLine } from "../components/misc/SwerveLine"
import Image from 'next/image';
import { PackViewsPure } from '../components/pack/PackViews';
import { AiOutlineEye, AiOutlinePlus, AiOutlinePlusCircle, AiOutlineSafety, AiOutlineShareAlt, AiOutlineUpload } from 'react-icons/ai';
import { MdTimeline } from 'react-icons/md';
import { BsFillMusicPlayerFill } from 'react-icons/bs';
import { CgMusicSpeaker } from 'react-icons/cg';

const ScrollAnimationStageTrigger = ({ setStage, index, children, ...rest }) => {
  const ref = useRef();
  const {
    inViewport
  } = useInViewport(
    ref,
    {},
    { disconnectOnLeave: false },
    {
      onEnterViewport: () => setStage(index),
      // onLeaveViewport: () => setStage(index )
    }

  );

  return <div {...rest} className="relative">
    <div ref={ref} className="absolute" style={{ bottom: "-10vh" }} />
    {children}
  </div>
}

const TextSection = ({ children, setStage, index, full, active, right, noMb, ...rest }) => {
  return <section className={`t-section flex align-middle flex-col py-4 ${!noMb && 'my-24'} md:py-0 md:my-0 md:px-16 justify-center ${full ? 'full' : ''}  `} {...rest}>
    <ScrollAnimationStageTrigger setStage={s => setStage && active && setStage(s)} index={index}>{children}</ScrollAnimationStageTrigger>
  </section>
}


const BREAKPOINTS = [0.125]
const componentPos = [
  { breakpoint: 0.125, mobile: { circleLeft: 10.5, circleTop: 10, left: 22, top: 120 }, circleLeft: 10.5, circleTop: 33.5, left: 14, bottom: 60 },
  { breakpoint: 0.28, mobile: { circleLeft: 18, circleTop: 30.5, left: 25, top: 350 }, circleLeft: 29.5, circleTop: 60, left: 16.5, top: 62 },
  { breakpoint: 0.555, mobile: { circleLeft: 14.5, circleTop: 50, left: 25, top: 600 }, circleLeft: 67, circleTop: 36, left: 55, bottom: 66 },
  { breakpoint: 0.745, mobile: { circleLeft: 13, circleTop: 75, left: 22, top: 875 }, circleLeft: 91, circleTop: 64, right: 10, top: 60, maxWidth: "20vw" },
  { breakpoint: 1000000 }
]

const getTextboxStyles = ({ left, top, bottom, right, maxWidth, mobile }, useMobile = false) => useMobile ? ({ left: mobile.left + '%', top: mobile.top, right: mobile.right + '%', bottom: mobile.bottom }) : ({ left: left + '%', top: top + '%', bottom: bottom + '%', right: right + '%', maxWidth })

const fakeViews = {
  last_dates: {
    1: new Date(new Date().setDate((new Date()).getDate() - 5)),
    2: new Date(),
    3: new Date(new Date().setDate((new Date()).getDate() - 1)),
    4: new Date(new Date().setDate((new Date()).getDate() - 2))
  },
  viewers: [
    { nickname: 'maxo', uid: 1 },
    { nickname: 'elias', uid: 2 },
    { nickname: 'alex-z', uid: 3 },
    { nickname: 'prod-nomass', uid: 4 },
  ]
}
const NumberCircle = ({ className, ...rest }) => <div className={className + ' bg-light-gray shadow-md text-black rounded-full w-11 h-11 flex items-center justify-center'} {...rest} />
const ProcessSection = ({ mobile }) => {

  const sectionRef = useRef()
  const [progress, setScrollInsideSection] = useState(0)
  const [progressUnbounded, setScrollInsideSectionUnbounded] = useState(0)
  useScrollPosition(({ prevPos, currPos }) => {


    const topScreen = -currPos.y

    const insideSection = Math.max(0, topScreen - (sectionRef?.current?.offsetTop || 0))

    const percentInsideSection = Math.min(1, (insideSection) / (sectionRef.current.clientHeight - window?.innerHeight))
    const minPercent = componentPos[0].breakpoint + percentInsideSection - .1
    if (progress !== minPercent) setScrollInsideSection((componentPos[0].breakpoint + minPercent * 0.8))

    setScrollInsideSectionUnbounded((componentPos[0].breakpoint + ((topScreen - (sectionRef?.current?.offsetTop || 0)) / (sectionRef.current.clientHeight - window?.innerHeight)) * 0.8))
  })

  const components = [
    { title: <span className='items-center flex'> <AiOutlineUpload className='mr-2' /> Upload your tracks</span>, text: <span>We auto-detect key & bpm. Access your tracks from anywhere in seconds.</span> },
    { title: <span className='items-center flex'><AiOutlinePlusCircle className='mr-2' /> Create a pack</span>, text: "Choose some tracks. Whether they're beats from 2019, or this week's loops, they're right here at your fingertips.", demoComponent: <div style={mobile ? { top: 425, left: '4%' } : { bottom: '35%', left: '34%' }} className={`${(mobile ? progressUnbounded > -1. : (progress >= componentPos[1].breakpoint)) ? "opacity-100" : "opacity-0 translate-y-2  "} delay-200 duration-500  transition-all absolute ${mobile ? 'w-36 h-36' : 'w-52 h-52'} `}><Image layout='fill' objectFit='contain' src={"/pack.png"} /></div> },
    { title: <span className='items-center flex'><AiOutlineShareAlt className='mr-2' /> Share your pack</span>, text: "Send out a link to your new pack, or set up automatic weeklies." },
    { title: <span className='items-center flex'><MdTimeline className='mr-2' /> Get insights</span>, text: "You’ll see when someone views your pack and which of your tracks are resonating best.", demoComponent: <PackViewsPure style={mobile ? { bottom: 180, right: '30%' } : { bottom: '50%', right: '5%' }} className={`absolute transition-opacity ${progress > 0.78 ? 'opacity-100' : ' opacity-0'}`} views={fakeViews} /> }
  ]



  if (mobile)
    return <Section className='h-auto -mx-10' ref={sectionRef}>
      <VerticalSwerveLine className={"w-full"}>
        <>
          {components.map((comp, idx) =>

            <>
              <Fade distance="20px" duration={500} bottom className={`transition-opacity `}>
                <div className='absolute text-left' style={getTextboxStyles(componentPos[idx], true)}>
                  <div className='-translate-y-1/2'>
                    <Subtitle className='text-left'>{comp.title}</Subtitle>
                    <Paragraph className='' style={{ maxWidth: '70vw' }}>{comp.text}</Paragraph>
                  </div>
                </div>
              </Fade>
              {comp.demoComponent}
              < NumberCircle className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: componentPos[idx].mobile.circleLeft + '%', top: componentPos[idx].mobile.circleTop + '%' }}>{idx + 1}</NumberCircle>
            </>
          )}
        </>
      </VerticalSwerveLine >
    </Section >
  return <Section style={{ height: 3200 }} className=' relative ' ref={sectionRef}>
    <SectionTitle className='ml-28'></SectionTitle>
    <SwerveLine className=" w-full sticky top-0 h-screen" completed={progress}>
      {components.map((comp, idx) => <div className={`transition-opacity ${progress >= componentPos[idx].breakpoint ? (progress < componentPos[idx + 1].breakpoint ? 'opacity-100' : 'opacity-25') : 'opacity-0'}`}>
        <div className='absolute text-left' style={getTextboxStyles(componentPos[idx])}>
          <Subtitle className='text-left'>{comp.title}</Subtitle>
          <Paragraph className='' style={{ maxWidth: '40vw' }}>{comp.text}</Paragraph>
        </div>
        {comp.demoComponent}
        <NumberCircle className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: componentPos[idx].circleLeft + '%', top: componentPos[idx].circleTop + '%' }}>{idx + 1}</NumberCircle>

      </div>)}
    </SwerveLine>

  </Section>
}

const CTASection = ({ mobile, ...rest }) => {
  const [email, setEmail] = useState("")
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState(null)
  const submit = async () => {
    if (validator.isEmail(email)) {
      setSending(true)
      await writeEmail(email)
      setResult("success")
      window.location.href = ('https://vxkh4xbs2tz.typeform.com/to/xmcfICkt')
      setSending(false)
    } else {
      setResult("bad_input")
      setTimeout(() => setResult(null), 3000)
    }
  }
  return <TextSection style={{ height: mobile && 'auto' }} noMb {...rest}>
    <SectionTitle>Sounds good?</SectionTitle>
    <Paragraph>
      A tool made for producers, by producers.
      <br />
      Try now with a forever-free tier.

      <span className="flex flex-col md:flex-row items-end mt-4 md:mt-8">
        {/* <Input style={{ width: mobile ? '100%' : 270 }} value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" full={mobile} type="email" className="form-control" aria-describedby="emailHelp" /> */}
        <Link href={'/create'}><Button style={{ height: 40 }} className="md:w-auto w-full justify-center items-center mt-4 md:mt-0 text-lg" primary>Enter beatpacks</Button></Link>
      </span>
    </Paragraph>
  </TextSection>
}

export default function Home() {
  const [demoStage, setDemoStage] = useState("welcome")

  const mobile = !useMediaQuery({
    query: '(min-width: 768px)'
  })

  const TextSections = [<TextSection key="personalized" setStage={setDemoStage} index={"personalized_packs"} active={!mobile} >
    <SectionTitle>Personalized packs</SectionTitle>
    <Paragraph>
      Make the receiver feel special.
      Upload your files once and compose them into personalized packs for your collaborators.
    </Paragraph>
  </TextSection>,
  <TextSection setStage={setDemoStage} key="boring" index={"boring_work"} active={!mobile}>
    <Fade distance="20px" duration={500} bottom>
      <div>
        <SectionTitle>We do the boring work</SectionTitle>
        <Paragraph>
          We auto-detect key & bpm, organize stems, and add all the information along with your “@” to the downloaded filenames.
        </Paragraph>
      </div>
    </Fade>
  </TextSection>]


  const DemoSection = <div className="flex demo-section md:sticky justify-center md:flex md:items-center md:pt-12 h-auto md:h-screen w-full md:w-1/2 top-0" style={{ marginBottom: !mobile && '-20vh' }}>
    <DemoPack stage={demoStage} />
  </div>

  const { phone, smallPhone } = useScreenSize()
  return (
    <div className={styles.container + ` ${mobile && 'px-10 h-auto'} bg-passive text-white`}>
      <Head>
        <title>Beatpacks</title>
        <meta name="description" content="Generated by create next app" />
        <link rel="icon" href="/favicon.ico" />
      </Head>

      <BackgroundScene className="z-0" />
      <div className='z-30'>
        <div className='fixed left-0 top-0 bg-passive w-full px-6 h-16 justify-between z-50 flex items-center'>
          <Logo className="text-2xl" />

          <Link href={'/packs'}><Button secondaryStatic className='text-white'>Enter beatpacks</Button></Link>
        </div>

        <Section full className="z-30 static flex justify-center items-center text-white">
          <ScrollAnimationStageTrigger setStage={setDemoStage} index={"welcome"} />


          <div className="z-30 flex items-center flex-col">
            <div className='relative '>
              <div className='absolute -top-40 left-1/2 -translate-x-1/2 w-full text-center'>
                <SectionTitle className="text-gray-100">Dropbox is for&nbsp;
                  <span className='relative opacity-90 overflow-visible'>
                    {/* {!smallPhone && <div className='opacity-70 absolute h-20 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -z-10 w-full ' >
                      <Image src={'/paperstack.png'} layout='fill' objectFit="contain" />
                    </div>} */}
                    documents.
                  </span></SectionTitle>

                {/* {smallPhone && <div className='opacity-80 absolute h-24 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -z-10 w-full ' >
                  <Image src={'/paperstack.png'} layout='fill' objectFit="contain" />
                </div>} */}

              </div>
              <Title className='text-shadow' >Beatpacks is for beats.</Title>
            </div>
            <Subtitle className="absolute m-auto bottom-6"> A tool for sending out beat & loop packs</Subtitle>

          </div>
        </Section>
        <div className="flex md:flex-row flex-col md:h-auto">
          {mobile && <ScrollAnimationStageTrigger setStage={setDemoStage} index={"personalized_packs"} />}
          {mobile && DemoSection}
          {mobile && <ScrollAnimationStageTrigger setStage={setDemoStage} index={"boring_work"} />}
          {/* <div className="md:w-1/2 w-full md:h-auto ">
            {TextSections}
          </div> */}

          {/* {!mobile && DemoSection} */}

        </div>

        <ProcessSection mobile={mobile}></ProcessSection>

        <TextSection key="personalized" index={"personalized_packs"} active={!mobile} >
          <SectionTitle className='flex items-center'><AiOutlineSafety className='mr-4' />Your music stays yours</SectionTitle>
          <Paragraph>
            All the analysis is done by algorithms, so no one gets to hear your music except the people you share it with. Not even us.
            <br/>
            We don't claim any rights of ownership to your tracks. 
            <br/>
            It's <b>your</b> music and we're just a platform to help you manage and share it.
          </Paragraph>
        </TextSection>

        <TextSection key="personalized" index={"personalized_packs"} active={!mobile} >
          <SectionTitle className='flex items-center'><CgMusicSpeaker className='mr-4' />Get more placements</SectionTitle>
          <Paragraph className='max-w-2xl'>
            We're optimizing for receiver experience. 
            <br/>
            Do you think the artist would rather see a convoluted dropbox folder with filenames that look like computer code, or a simple clean page with the tracks you picked for them?
            <br/>
            <span className='  '>Give your music the presentation it deserves.</span>
          </Paragraph>
        </TextSection>

        <CTASection mobile={mobile} full={mobile} />
      </div>
      {/* 
      <CookieConsent
      buttonStyle={{borderRadius: 4}}
      >This website uses cookies to enhance the user experience.</CookieConsent> */}
    </div>
  )
}

removeLayout(Home)